import { useEffect, useState } from "react";
import { AlertTriangle, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useI18n } from "@/lib/i18n";

/**
 * Sticky red banner shown across ANY surface when the current tab is in
 * an admin-driven impersonation session (student or instructor). The flag
 * is persisted in sessionStorage under `admin_impersonation` and is
 * captured from the URL on the magic-link landing.
 *
 * Clicking "End impersonation" signs out the impersonated session and
 * returns to the admin console. The admin's original tab is untouched
 * because impersonation always opens in a NEW tab.
 */
export default function AdminImpersonationBanner() {
  const { t, isRTL } = useI18n();
  const [info, setInfo] = useState(null);

  useEffect(() => {
    // 1) Capture from URL if magic-link just landed.
    try {
      const url = new URL(window.location.href);
      const isImp = url.searchParams.get("admin_impersonation") === "1";
      if (isImp) {
        const next = {
          actor: url.searchParams.get("actor") || "admin",
          role: url.searchParams.get("role") || "student",
          academy_id: url.searchParams.get("academy_id") || "",
          academy_slug: url.searchParams.get("academy_slug") || "",
          academy_name: url.searchParams.get("academy_name") || "",
          target_name: url.searchParams.get("target_name") || "",
          started_at: new Date().toISOString(),
        };
        sessionStorage.setItem("admin_impersonation", JSON.stringify(next));
        // Clean URL so the flag isn't shareable / re-triggered.
        url.searchParams.delete("admin_impersonation");
        url.searchParams.delete("actor");
        url.searchParams.delete("role");
        url.searchParams.delete("academy_id");
        url.searchParams.delete("academy_slug");
        url.searchParams.delete("academy_name");
        url.searchParams.delete("target_name");
        window.history.replaceState({}, "", url.pathname + (url.search ? `?${url.searchParams}` : "") + url.hash);
        setInfo(next);
        return;
      }
    } catch {}

    // 2) Otherwise read the persisted session.
    try {
      const raw = sessionStorage.getItem("admin_impersonation");
      if (raw) setInfo(JSON.parse(raw));
    } catch {}
  }, []);

  if (!info) return null;

  const handleExit = async () => {
    try { sessionStorage.removeItem("admin_impersonation"); } catch {}
    try { await supabase.auth.signOut(); } catch {}
    window.location.href = info.role === "instructor"
      ? `/admin/academies/${info.academy_id || ""}`
      : "/admin/students";
  };

  const targetLabel = info.role === "instructor"
    ? t("admin.impersonation.banner.instructor", "المدرّب")
    : t("admin.impersonation.banner.student", "الطالب");

  return (
    <div
      className="sticky top-0 z-[60] bg-red-600 text-white shadow-md"
      dir={isRTL ? "rtl" : "ltr"}
      role="alert"
    >
      <div className="max-w-[1440px] mx-auto px-3 sm:px-4 py-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-7 h-7 rounded-full bg-white/20 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-4 h-4" />
          </div>
          <div className="min-w-0 leading-tight">
            <p className="text-[13px] font-semibold truncate">
              {t("admin.impersonation.banner.title", "أنت الآن تحاكي بصفة Admin")}
            </p>
            <p className="text-[11px] text-white/90 truncate">
              {targetLabel}: <span className="font-semibold">{decodeURIComponent(info.target_name || "")}</span>
              {info.academy_name ? (
                <>
                  {" "}|{" "}
                  {t("admin.impersonation.banner.academy", "الأكاديمية")}:{" "}
                  <span className="font-semibold">{decodeURIComponent(info.academy_name)}</span>
                </>
              ) : null}
            </p>
          </div>
        </div>
        <Button
          size="sm"
          variant="secondary"
          className="h-8 gap-1.5 rounded-full text-destructive hover:text-destructive/80 shrink-0"
          onClick={handleExit}
        >
          <LogOut className="w-3.5 h-3.5" />
          <span className="hidden sm:inline">
            {t("admin.impersonation.banner.exit", "إنهاء المحاكاة")}
          </span>
        </Button>
      </div>
    </div>
  );
}